import React, { useEffect, useRef, useState } from 'react';

import { ia, iaRa, mString } from 'lib/helpers/utility';

import Button from '../Button/Button';
import Typography from '../Typography/Typography';

const sumGroupValues = (api, groupNode, column) => {
  let sum = 0;
  groupNode.allLeafChildren.forEach((childNode) => {
    const columnDef = column.getColDef();
    const cellValue = columnDef.valueGetter
      ? columnDef.valueGetter({ data: childNode.data, colDef: columnDef })
      : childNode.data[columnDef.field];

    if (typeof cellValue === 'number') {
      sum += cellValue;
    }
  });
  return sum;
};

const getValuesFromSelectedCells = (api, range) => {
  const values = {};

  if (range && range.columns && range.startRow && range.endRow) {
    range.columns.forEach((column) => {
      const columnId = column.getColId();
      if (!values[columnId]) values[columnId] = [];

      const startRowIndex = range.startRow.rowIndex;
      const endRowIndex = range.endRow.rowIndex;

      const minRowIndex = Math.min(startRowIndex, endRowIndex);
      const maxRowIndex = Math.max(startRowIndex, endRowIndex);

      if (startRowIndex > endRowIndex) {
        for (let rowIndex = maxRowIndex; rowIndex >= minRowIndex; rowIndex--) {
          const rowNode = api.getDisplayedRowAtIndex(rowIndex);

          if (rowNode) {
            const columnDef = column.getColDef();
            const cellValue = rowNode.group
              ? sumGroupValues(api, rowNode, column)
              : columnDef.valueGetter
                ? columnDef.valueGetter({ data: rowNode.data, colDef: columnDef })
                : rowNode.data[columnDef.field];

            if (typeof cellValue === 'number') {
              values[columnId].push(cellValue);
            }
          }
        }
        return;
      }

      for (let rowIndex = minRowIndex; rowIndex <= maxRowIndex; rowIndex++) {
        const rowNode = api.getDisplayedRowAtIndex(rowIndex);

        if (rowNode) {
          const columnDef = column.getColDef();

          const cellValue = rowNode.group
            ? sumGroupValues(api, rowNode, column)
            : columnDef.valueGetter
              ? columnDef.valueGetter({ data: rowNode.data, colDef: columnDef })
              : rowNode.data[columnDef.field];

          if (typeof cellValue === 'number') {
            values[columnId].push(cellValue);
          }
        }
      }
    });
  }

  return values;
};

const CustomStatusBarAggregationComponentTooltip = ({ otherElements }) => {
  return (
    <div className="flex flex-col gap-1 px-1">
      {otherElements.map((element) => (
        <div key={element.field} className="flex items-center justify-between gap-2">
          <Typography className="text-nowrap" variant="p" size="small" color="white">
            {element.label}:
          </Typography>
          <Typography
            className="text-neutral-white text-nowrap"
            variant="p"
            size="small"
            color="white">
            {element.field?.endsWith('_') ? element?.value : mString(element.value)}
          </Typography>
        </div>
      ))}
    </div>
  );
};

const CustomStatusBarAggregationComponent = (props) => {
  const { statusPanelData = [], api, columnApi } = props;
  const [finalStatusPanelData, setFinalStatusPanelData] = useState(iaRa(statusPanelData));
  const [hasActiveSelection, setHasActiveSelection] = useState(false);

  const eventListenerRef = useRef(false);

  const [firstElement] = finalStatusPanelData.slice(0, 1);
  const otherElements = finalStatusPanelData.slice(1);

  const selectedElements = otherElements.filter((element) => element.value !== null);

  const tooltipElements = hasActiveSelection ? selectedElements : otherElements;

  useEffect(() => {
    setFinalStatusPanelData(statusPanelData);
    setHasActiveSelection(false);
  }, [statusPanelData]);

  useEffect(() => {
    let columnSums = {};

    const updateMetrics = () => {
      columnSums = {};
      const cellRanges = api.getCellRanges();

      if (cellRanges && cellRanges.length > 0) {
        if (
          cellRanges[0].startRow.rowIndex === cellRanges[0].endRow.rowIndex &&
          cellRanges[0].columns.length === 1
        ) {
          setFinalStatusPanelData(statusPanelData);
          setHasActiveSelection(false);
          return;
        }

        setHasActiveSelection(true);

        cellRanges.forEach((range) => {
          const rangeValues = getValuesFromSelectedCells(api, range);

          Object.entries(rangeValues).forEach(([columnId, values]) => {
            const formattedColumnId = columnId.toLowerCase();
            if (!columnSums[formattedColumnId]) {
              columnSums[formattedColumnId] = 0;
            }

            columnSums[formattedColumnId] += values.reduce((acc, curr) => acc + curr, 0);
          });
        });

        const updatedStatusPanelData = statusPanelData.map(({ field, label }) => {
          const newValue = columnSums[field.toLowerCase()] ?? null;
          return { field, label, value: newValue };
        });

        const withValues = updatedStatusPanelData.filter((item) => item.value !== null);
        const withoutValues = updatedStatusPanelData.filter((item) => item.value === null);
        setFinalStatusPanelData([...withValues, ...withoutValues]);
      } else {
        setFinalStatusPanelData(statusPanelData);
        setHasActiveSelection(false);
      }
    };

    if (!eventListenerRef.current) {
      api.addEventListener('rangeSelectionChanged', updateMetrics);
      eventListenerRef.current = true;
    }

    return () => {
      api.removeEventListener('rangeSelectionChanged', updateMetrics);
    };
  }, [api, columnApi, statusPanelData]);

  return (
    <div className="flex h-full items-center gap-1 sm:hidden">
      {firstElement && (
        <>
          <Typography variant="label" size="small" color="neutral-500">
            {firstElement.label}:
          </Typography>
          <Typography variant="label" size="small" weight="medium" color="neutral-700">
            {firstElement.field?.endsWith('_') ? firstElement?.value : mString(firstElement.value)}
          </Typography>
          {ia(tooltipElements) && (!hasActiveSelection || ia(selectedElements)) && (
            <Button
              icon="info-mono"
              size="xs"
              className="bg-neutral-75 hover:bg-neutral-200 active:bg-neutral-300"
              iconColor="neutral-700"
              tooltip={
                <CustomStatusBarAggregationComponentTooltip otherElements={tooltipElements} />
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default CustomStatusBarAggregationComponent;
